import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  username?: string;
  tfaEnabled: boolean;
  role?: number;
  isAdmin?: boolean;
  isRoot?: boolean;
  profileImage?: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: User) => void;
  refreshUser: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  isAuthenticated: boolean;
  isLoading: boolean;
  requires2FA: boolean;
  tempToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'ems_access_token';
const REFRESH_TOKEN_KEY = 'ems_refresh_token';
const USER_KEY = 'ems_user';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);

  // Load tokens and user from localStorage and validate refresh token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      // If we have a refresh token, validate it by trying to refresh the access token
      if (storedRefreshToken) {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
          const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken: storedRefreshToken }),
          });

          const data = await response.json();

          if (response.ok) {
            // Refresh token is valid - update tokens
            setAccessToken(data.accessToken);
            setRefreshToken(data.refreshToken);
            localStorage.setItem(TOKEN_KEY, data.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
            
            // Load or fetch user data
            if (storedUser) {
              try {
                setUser(JSON.parse(storedUser));
              } catch (error) {
                console.error('Error loading user from storage:', error);
                // Fetch user data from API
                try {
                  const userResponse = await fetch(`${API_BASE_URL}/auth/me`, {
                    headers: {
                      'Authorization': `Bearer ${data.accessToken}`,
                      'Content-Type': 'application/json',
                    },
                  });
                  if (userResponse.ok) {
                    const userData = await userResponse.json();
                    if (userData.success && userData.user) {
                      setUser(userData.user);
                      localStorage.setItem(USER_KEY, JSON.stringify(userData.user));
                    }
                  }
                } catch (userError) {
                  console.error('Error fetching user data:', userError);
                }
              }
            } else {
              // Fetch user data from API
              try {
                const userResponse = await fetch(`${API_BASE_URL}/auth/me`, {
                  headers: {
                    'Authorization': `Bearer ${data.accessToken}`,
                    'Content-Type': 'application/json',
                  },
                });
                if (userResponse.ok) {
                  const userData = await userResponse.json();
                  if (userData.success && userData.user) {
                    setUser(userData.user);
                    localStorage.setItem(USER_KEY, JSON.stringify(userData.user));
                  }
                }
              } catch (userError) {
                console.error('Error fetching user data:', userError);
              }
            }
          } else {
            // Refresh token is invalid or expired
            setUser(null);
            setAccessToken(null);
            setRefreshToken(null);
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          }
        } catch (error) {
          // Network error (server restarting, offline) — keep existing session
          console.warn('Could not reach auth server, keeping existing session:', (error as Error).message);
          if (storedToken && storedUser) {
            try {
              setAccessToken(storedToken);
              setUser(JSON.parse(storedUser));
            } catch {
              // Stored user data is corrupt — clear
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              localStorage.removeItem(USER_KEY);
            }
          }
        }
      } else if (storedToken && storedUser) {
        // We have access token but no refresh token (legacy case)
        try {
          setAccessToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Error loading user from storage:', error);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle rate limiting error
        if (response.status === 429) {
          throw new Error(data.message || 'Too many login attempts. Please try again after 15 minutes.');
        }
        throw new Error(data.message || data.error || 'Login failed');
      }

      if (data.requires2FA && data.tempToken) {
        setRequires2FA(true);
        setTempToken(data.tempToken);
        setIsLoading(false);
        return;
      }

      // No 2FA required
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setUser(data.user);
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      }
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setRequires2FA(false);
      setTempToken(null);
      setIsLoading(false);
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      throw error;
    }
  }, []);

  const verify2FA = useCallback(async (tempToken: string, code: string) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tempToken, code }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '2FA verification failed');
      }

      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setUser(data.user);
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      }
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setRequires2FA(false);
      setTempToken(null);
      setIsLoading(false);
    } catch (error) {
      console.error('2FA verification error:', error);
      setIsLoading(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend to invalidate refresh token
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem(TOKEN_KEY);
      
      if (token) {
        try {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          // Continue with logout even if API call fails
          console.error('Error calling logout endpoint:', error);
        }
      }
    } catch (error) {
      // Continue with logout even if there's an error
      console.error('Logout error:', error);
    } finally {
      // Always clear frontend state
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
      setRequires2FA(false);
      setTempToken(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  }, []);

  const updateUser = useCallback((userData: User) => {
    setUser(userData);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem(TOKEN_KEY);
      
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        }
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (!storedRefreshToken) {
        // No refresh token, clear everything
        setUser(null);
        setAccessToken(null);
        setRefreshToken(null);
        setRequires2FA(false);
        setTempToken(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Refresh token is invalid or expired
        setUser(null);
        setAccessToken(null);
        setRefreshToken(null);
        setRequires2FA(false);
        setTempToken(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return false;
      }

      // Update tokens
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      
      return true;
    } catch (error) {
      // Network error — don't log out, let the caller retry
      console.warn('Token refresh network error (server may be restarting):', (error as Error).message);
      return false;
    }
  }, []);

  const value: AuthContextType = useMemo(() => ({
    user,
    accessToken,
    refreshToken,
    login,
    verify2FA,
    logout,
    updateUser,
    refreshUser,
    refreshAccessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    requires2FA,
    tempToken: tempToken || null,
  }), [user, accessToken, refreshToken, login, verify2FA, logout, updateUser, refreshUser, refreshAccessToken, isLoading, requires2FA, tempToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

